import { useState, useEffect } from 'react';
import { Container, Header, Content, Navbar, Table, Button, Input, InputGroup, Panel, Message, Badge } from 'rsuite';
import { Search } from '@rsuite/icons';
import { getApiUrl, getAuthHeaders, getUserId } from './utils/apiConfig';
import { useAuth } from './utils/useAuth';
import DateDisplay from './components/DateDisplay';
import { formatDateTime } from './utils/dateUtils';
import CopyButton from './components/CopyButton';

const { Column, HeaderCell, Cell } = Table;

const fetchSpendings = async (getAccessTokenSilently, user) => {
  // Don't try to fetch if getAccessTokenSilently is not available
  if (!getAccessTokenSilently) {
    throw new Error('Authentication required. Auth0 token not available.');
  }
  
  const userId = getUserId(user);
  if (!userId) {
    throw new Error('User ID not found. Please set up your user ID first.');
  }
  
  const headers = await getAuthHeaders(getAccessTokenSilently);
  const response = await fetch(getApiUrl(`/api/users/me/spendings`), {
    method: "GET",
    headers: headers,
  });
  
  if (response.status === 401) {
    throw new Error('Unauthorized - Authentication required');
  }
  
  if (response.status === 503) {
    throw new Error('Billing is not configured');
  }
  
  if (!response.ok) {
    throw new Error(`Failed to fetch spendings: ${response.statusText}`);
  }
  
  const data = await response.json();
  return data;
};

const formatAmount = (amount, currency = 'usd') => {
  if (amount === null || amount === undefined) return 'N/A';
  const sign = amount < 0 ? '-' : '';
  const absAmount = Math.abs(amount);
  return `${sign}$${absAmount.toFixed(2)}`;
};

function Billing() {
  const auth = useAuth();
  const [spendings, setSpendings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  // Trigger refresh when auth becomes ready
  useEffect(() => {
    if (auth.hasAuth0 && !auth.isLoading && auth.isAuthenticated && auth.getAccessTokenSilently) {
      setRefreshKey(prev => prev + 1);
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.hasAuth0]);

  // Check for payment success/cancel in URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');
    if (paymentStatus === 'success') {
      // Refresh spendings after successful payment
      setRefreshKey(prev => prev + 1);
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname + window.location.hash.split('?')[0]);
    }
  }, []);

  useEffect(() => {
    // Only load spendings if Auth0 is ready and user is authenticated
    if (auth.hasAuth0 && !auth.isLoading && auth.isAuthenticated && auth.getAccessTokenSilently) {
      loadSpendings();
    } else if (!auth.hasAuth0) {
      // If Auth0 is not configured, still try to load (might use API key)
      loadSpendings();
    }
  }, [refreshKey, auth.isLoading, auth.isAuthenticated, auth.hasAuth0]);

  const loadSpendings = async () => {
    // Don't try to load if Auth0 is still loading or user is not authenticated
    if (auth.hasAuth0 && (auth.isLoading || !auth.isAuthenticated || !auth.getAccessTokenSilently)) {
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSpendings(auth.getAccessTokenSilently, auth.user);
      setSpendings(data.spendings || []);
    } catch (err) {
      console.error('Error loading spendings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredSpendings = spendings.filter(spending => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      spending.description?.toLowerCase().includes(searchLower) ||
      spending.transaction_id?.toLowerCase().includes(searchLower) ||
      spending.type?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <Container>
      <Header style={{ position: 'sticky', top: 0, zIndex: 1000 }}>
        <Navbar style={{ display: 'flex', alignItems: 'center' }}>
          <Navbar.Brand>
            Billing & Spending
          </Navbar.Brand>
          <div style={{ marginLeft: 'auto', marginRight: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <InputGroup inside style={{ width: 300 }}>
              <Input
                placeholder="Search spendings..."
                value={searchTerm}
                onChange={(value) => setSearchTerm(value)}
              />
              <InputGroup.Addon>
                <Search />
              </InputGroup.Addon>
            </InputGroup>
            <Button appearance="primary" onClick={loadSpendings} loading={loading}>
              Refresh
            </Button>
          </div>
        </Navbar>
      </Header>
      <Content>
        <div style={{ padding: '20px' }}>
          <Panel>

          {error && (
            <Message type="error" closable onClose={() => setError(null)}>
              {error}
            </Message>
          )}

          {loading && spendings.length === 0 ? (
            <div>Loading spendings...</div>
          ) : filteredSpendings.length === 0 ? (
            <Message type="info">
              {searchTerm ? 'No spendings found matching your search.' : 'No spending history found.'}
            </Message>
          ) : (
            <Table
              data={filteredSpendings}
              autoHeight
              rowHeight={60}
              loading={loading}
            >
              <Column width={180}>
                <HeaderCell>Date</HeaderCell>
                <Cell>
                  {(rowData) => <DateDisplay isoString={rowData.created_at} />}
                </Cell>
              </Column>

              <Column flexGrow={1} minWidth={200}>
                <HeaderCell>Description</HeaderCell>
                <Cell>
                  {(rowData) => (
                    <span>{rowData.description || <em style={{ color: '#999' }}>No description</em>}</span>
                  )}
                </Cell>
              </Column>

              <Column width={120}>
                <HeaderCell>Type</HeaderCell>
                <Cell>
                  {(rowData) => {
                    const type = rowData.type || 'unknown';
                    const colorMap = {
                      'charge': 'red',
                      'payment': 'green',
                      'refund': 'orange',
                      'adjustment': 'blue',
                    };
                    return (
                      <Badge content={type} color={colorMap[type.toLowerCase()] || 'gray'} />
                    );
                  }}
                </Cell>
              </Column>

              <Column width={150}>
                <HeaderCell>Amount</HeaderCell>
                <Cell>
                  {(rowData) => {
                    const amount = rowData.amount || 0;
                    const color = amount < 0 ? '#ff4d4f' : '#52c41a';
                    return (
                      <span style={{ fontWeight: 'bold', color }}>
                        {formatAmount(amount, rowData.currency)}
                      </span>
                    );
                  }}
                </Cell>
              </Column>

              <Column flexGrow={1} minWidth={260}>
                <HeaderCell>Transaction ID</HeaderCell>
                <Cell>
                  {(rowData) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.9em', wordBreak: 'break-all' }}>
                      {rowData.transaction_id || '-'}
                    </span>
                      {rowData.transaction_id && (
                        <CopyButton
                          text={rowData.transaction_id}
                          size="sm"
                          successMessage="Transaction ID copied"
                          errorMessage="Failed to copy transaction ID"
                        />
                      )}
                    </div>
                  )}
                </Cell>
              </Column>
            </Table>
          )}

          {filteredSpendings.length > 0 && (
            <div style={{ marginTop: 10, color: '#999', fontSize: '0.9em' }}>
              Showing {filteredSpendings.length} of {spendings.length} transaction{spendings.length !== 1 ? 's' : ''}
            </div>
          )}
          </Panel>
        </div>
      </Content>
    </Container>
  );
}

export default Billing;

